import { useEffect, useMemo, useState } from 'react';

export function parseKpiNumeric(value) {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '—') return null;
  const cleaned = raw.replace(/,/g, '').replace(/%$/, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Whether a KPI value should render with a trailing percent sign. */
export function kpiValueUsesPercent(value) {
  return String(value ?? '').trim().endsWith('%');
}

/** @returns {number | null} decimal places to show while animating, or null for integers */
export function kpiValueDecimalPlaces(value) {
  if (!kpiValueUsesPercent(value)) return null;
  const raw = String(value ?? '').trim().replace(/%$/, '');
  return raw.includes('.') ? 1 : null;
}

function formatKpiDisplay(n, { suffix = '', decimals = null } = {}) {
  if (decimals != null) {
    return `${n.toFixed(decimals)}${suffix}`;
  }
  return `${Math.round(n)}${suffix}`;
}

/** Parse signed percent labels such as +12% or -5%. */
export function parseKpiPercent(value) {
  const raw = String(value ?? '').trim();
  const match = raw.match(/^([+-]?)(\d+(?:\.\d+)?)%$/);
  if (!match) return null;
  return {
    sign: match[1],
    magnitude: Number(match[2]),
    decimals: match[2].includes('.') ? 1 : null,
  };
}

export function formatKpiPercent(sign, magnitude, decimals = null) {
  const formatted = decimals != null ? magnitude.toFixed(decimals) : String(magnitude);
  if (Number(magnitude) === 0 && !sign) return '0%';
  if (sign === '+') return `+${formatted}%`;
  if (sign === '-') return `-${formatted}%`;
  return `${formatted}%`;
}

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * Count up to a numeric KPI value on mount / when value changes.
 * @param {string|number} value
 * @param {{ duration?: number, delay?: number, animate?: boolean }} options
 */
export function useKpiCountUp(value, { duration = 680, delay = 0, animate = true } = {}) {
  const target = parseKpiNumeric(value);
  const isNumeric = target !== null;
  const suffix = kpiValueUsesPercent(value) ? '%' : '';
  const decimals = kpiValueDecimalPlaces(value);

  const formatDisplay = useMemo(
    () => (n) => formatKpiDisplay(n, { suffix, decimals }),
    [suffix, decimals],
  );

  const [display, setDisplay] = useState(() => {
    if (!animate || !isNumeric || prefersReducedMotion()) return String(value ?? '');
    return formatDisplay(0);
  });

  useEffect(() => {
    if (!isNumeric) {
      setDisplay(String(value ?? ''));
      return undefined;
    }

    if (!animate || prefersReducedMotion()) {
      setDisplay(String(value ?? ''));
      return undefined;
    }

    let rafId = 0;
    let timeoutId = 0;
    setDisplay(formatDisplay(0));

    timeoutId = window.setTimeout(() => {
      const startAt = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - startAt) / duration);
        const eased = 1 - (1 - progress) ** 3;
        setDisplay(formatDisplay(target * eased));
        if (progress < 1) {
          rafId = requestAnimationFrame(tick);
        } else {
          setDisplay(String(value ?? ''));
        }
      };
      rafId = requestAnimationFrame(tick);
    }, delay);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [value, target, isNumeric, animate, duration, delay, formatDisplay]);

  return { display, isNumeric };
}
